import React from 'react';

// True when the observed element is TALLER than the viewport space available to it — i.e. pinning it
// would force it to grow its own scrollbar.
//
// The sticky filter rail is capped at the viewport height with overflow-y: auto, so that a pinned
// rail taller than the screen doesn't hide its own bottom. The side effect is a second scrollbar
// whenever there are many facets (common when a narrow result set still offers every attribute).
// Callers use this to drop the pin in exactly that case, letting the rail scroll with the page.
//
// `offset` is the space above the element (the sticky top offset), measured live so a pinned header
// of any height is accounted for.
export function useFitsViewport(offset: () => number) {
    const ref = React.useRef<HTMLElement | null>(null);
    const [overflows, setOverflows] = React.useState(false);

    const measure = React.useCallback(() => {
        const el = ref.current;
        if (!el) return;
        // scrollHeight, not clientHeight: once capped, the element's own height stops telling us how
        // tall its content actually wants to be.
        const available = window.innerHeight - offset();
        setOverflows(el.scrollHeight > available);
    }, [offset]);

    React.useEffect(() => {
        measure();
        const el = ref.current;
        if (!el) return;
        // The rail's height changes as facet groups load, and the available height changes on resize.
        const ro = new ResizeObserver(() => measure());
        ro.observe(el);
        window.addEventListener('resize', measure);
        return () => {
            ro.disconnect();
            window.removeEventListener('resize', measure);
        };
    }, [measure]);

    return { ref, overflows };
}
